/**
 * EVERY FIGURE ON THE SITE THAT CHANGES ON A SCHEDULE, IN ONE PLACE.
 *
 * Read UPDATING.md at the repository root before editing: it says where each
 * number comes from, when it changes, and which tests prove the site agrees
 * with this file. Nothing below is a default input or a historical series;
 * those live with their calculators and in historicalData.js.
 *
 * Money is in whole pounds. Percentages are plain numbers (20 means 20%).
 */

const figures = {
  /** Bump `on` every time you check the figures, even if nothing changed. */
  verified: {
    on: "2026-09-04",
    taxYear: "2026/27",
    label: "September 2026",
  },

  // ── Income tax ───────────────────────────────────────────────────────
  // GOV.UK "Income Tax rates and allowances" and "Scottish Income Tax".
  // Thresholds are frozen to April 2028 (HM Treasury).
  incomeTax: {
    personalAllowance: 12570,
    taperThreshold: 100000,
    taperRatePerPoundLost: 2,
    basicRateBandWidth: 37700,
    basicRatePercent: 20,
    higherRatePercent: 40,
    additionalRatePercent: 45,
    /** Taxable income bands (after the personal allowance). */
    bands: {
      englandWalesNI: [
        { name: "Basic Rate (20%)", from: 0, to: 37700, rate: 20 },
        { name: "Higher Rate (40%)", from: 37700, to: 125140, rate: 40 },
        { name: "Additional Rate (45%)", from: 125140, to: Infinity, rate: 45 },
      ],
      scotland: [
        { name: "Starter Rate (19%)", from: 0, to: 3967, rate: 19 },
        { name: "Basic Rate (20%)", from: 3967, to: 16956, rate: 20 },
        { name: "Intermediate Rate (21%)", from: 16956, to: 31092, rate: 21 },
        { name: "Higher Rate (42%)", from: 31092, to: 62430, rate: 42 },
        { name: "Advanced Rate (45%)", from: 62430, to: 125140, rate: 45 },
        { name: "Top Rate (48%)", from: 125140, to: Infinity, rate: 48 },
      ],
    },
  },

  // ── National Insurance (employee, Class 1) ───────────────────────────
  // GOV.UK "Rates and allowances: National Insurance contributions".
  nationalInsurance: {
    primaryThreshold: 12570,
    upperEarningsLimit: 50270,
    mainRatePercent: 8,
    upperRatePercent: 2,
    /** Employer (secondary) Class 1: 15% above £5,000 from April 2025. */
    employerSecondaryThreshold: 5000,
    employerRatePercent: 15,
  },
  // ── Marriage Allowance ───────────────────────────────────────────────
  // GOV.UK "Marriage Allowance": 10% of the personal allowance moves from
  // the lower earner to their partner.
  marriageAllowanceTransfer: 1260,
  // ── Workplace pension auto-enrolment band ────────────────────────────
  // DWP "Automatic enrolment: review of the earnings trigger and qualifying
  // earnings band" (published each spring; frozen again for 2026/27).
  autoEnrolment: {
    lowerQualifyingEarnings: 6240,
    upperQualifyingEarnings: 50270,
    earningsTrigger: 10000,
  },

  // ── Student loans: this year's thresholds and interest ───────────────
  // GOV.UK "Repaying your student loan" (thresholds, April) and SLC interest
  // announcements (September). historicalData.js must carry the same
  // numbers under this tax year; figuresTripwire.test.js checks.
  studentLoans: {
    rpiPercent: 4.1,
    plan1: {
      threshold: 26900,
      repaymentPercent: 9,
      interestPercent: 4.1,
      interestLabel: "4.1%",
    },
    plan2: {
      threshold: 29385,
      upperThreshold: 52885,
      repaymentPercent: 9,
      interestPercent: 6.0,
      interestLabel: "6.0% (max)",
    },
    plan4: {
      threshold: 33795,
      repaymentPercent: 9,
      interestPercent: 4.1,
      interestLabel: "4.1%",
    },
    plan5: {
      threshold: 25000,
      repaymentPercent: 9,
      interestPercent: 4.1,
      interestLabel: "4.1%",
    },
    postgrad: {
      threshold: 21000,
      repaymentPercent: 6,
      interestPercent: 6.0,
      interestLabel: "6.0% (max)",
    },
  },

  // ── Pension tax relief ───────────────────────────────────────────────
  // GOV.UK "Tax on your private pension contributions".
  pensionRelief: {
    nonTaxpayerGrossLimit: 3600,
    nonTaxpayerNetCost: 2880,
    nonTaxpayerTopUp: 720,
    basicRateReliefPercent: 20,
  },

  // ── Lifetime ISA ─────────────────────────────────────────────────────
  // GOV.UK "Lifetime ISA". Only the property cap is legislated to 2031.
  lisa: {
    minOpenAge: 18,
    maxOpenAge: 39,
    maxContributionAge: 50,
    penaltyFreeAge: 60,
    annualContributionCap: 4000,
    overallIsaAllowance: 20000,
    bonusRate: 0.25,
    annualBonusCap: 1000,
    maxPropertyPrice: 450000,
    minAccountOpenMonths: 12,
    withdrawalChargeRate: 0.25,
    effectiveOwnMoneyLossRate: 0.0625,
    maxBuyersPerProperty: 2,
    rulesConfirmedUntil: "April 2031",
    sourceCheckedOn: "2026-09-01",
  },

  // ── Property transaction taxes ───────────────────────────────────────
  // GOV.UK SDLT rates; Revenue Scotland LBTT; Welsh Revenue Authority LTT.
  propertyTax: {
    sdlt: {
      standard: [
        { from: 0, to: 125000, rate: 0 },
        { from: 125000, to: 250000, rate: 2 },
        { from: 250000, to: 925000, rate: 5 },
        { from: 925000, to: 1500000, rate: 10 },
        { from: 1500000, to: Infinity, rate: 12 },
      ],
      /** Standard bands plus the 5% additional-property surcharge. */
      additional: [
        { from: 0, to: 125000, rate: 5 },
        { from: 125000, to: 250000, rate: 7 },
        { from: 250000, to: 925000, rate: 10 },
        { from: 925000, to: 1500000, rate: 15 },
        { from: 1500000, to: Infinity, rate: 17 },
      ],
      /** First-time buyer relief: 0% to nilBand, bandRate to the ceiling, none above. */
      firstTimeBuyer: { nilBand: 300000, reliefCeiling: 500000, bandRate: 5 },
    },
    lbtt: {
      standard: [
        { from: 0, to: 145000, rate: 0 },
        { from: 145000, to: 250000, rate: 2 },
        { from: 250000, to: 325000, rate: 5 },
        { from: 325000, to: 750000, rate: 10 },
        { from: 750000, to: Infinity, rate: 12 },
      ],
      firstTimeBuyer: [
        { from: 0, to: 175000, rate: 0 },
        { from: 175000, to: 250000, rate: 2 },
        { from: 250000, to: 325000, rate: 5 },
        { from: 325000, to: 750000, rate: 10 },
        { from: 750000, to: Infinity, rate: 12 },
      ],
      /** Additional Dwelling Supplement, flat on the whole price. */
      adsPercent: 8,
    },
    ltt: {
      standard: [
        { from: 0, to: 225000, rate: 0 },
        { from: 225000, to: 400000, rate: 6 },
        { from: 400000, to: 750000, rate: 7.5 },
        { from: 750000, to: 1500000, rate: 10 },
        { from: 1500000, to: Infinity, rate: 12 },
      ],
      additional: [
        { from: 0, to: 180000, rate: 5 },
        { from: 180000, to: 250000, rate: 8.5 },
        { from: 250000, to: 400000, rate: 10 },
        { from: 400000, to: 750000, rate: 12.5 },
        { from: 750000, to: 1500000, rate: 15 },
        { from: 1500000, to: Infinity, rate: 17 },
      ],
    },
  },

  // ── Savings and retirement allowances ─────────────────────────────────
  /** Adult ISA subscription limit for the tax year. Source: GOV.UK "Individual Savings Accounts". */
  isa: { annualAllowance: 20000 },
  /** Pension annual allowance (standard, before tapering). Source: GOV.UK "Tax on your private pension contributions". */
  pensionAnnualAllowance: 60000,
  /**
   * Full new State Pension. Source: GOV.UK "The new State Pension: what you'll
   * get" (£241.30 a week from April 2026). Yearly is 52 weeks, rounded.
   */
  statePension: { fullNewWeekly: 241.3, fullNewYearly: 12548, age: 67 },

  /**
   * Child Benefit and the High Income Child Benefit Charge. Source: GOV.UK
   * "Child Benefit: what you'll get" and "High Income Child Benefit Charge".
   * The charge is 1% of the Child Benefit for every £200 of adjusted net
   * income over chargeStart, so it is all gone at chargeEnd.
   */
  childBenefit: {
    eldestWeekly: 27.05,
    additionalWeekly: 17.9,
    chargeStart: 60000,
    chargeEnd: 80000,
  },

};

export default figures;
